import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { GraviGrid, useGridFetch, validateFloat, validateInt, validateNotEmptyString } from '@gravitate-js/excalibrr'
import api from '@utils/api'
import React, { useEffect, useState, useMemo, useCallback } from 'react'

import { createTrailerConfig } from './createConfig'
import { useUserSettings } from '@hooks/settings'
import { useTrailers } from '@api/useTrailers'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'trailer'

export function Trailer() {
  const [selectOptions, setSelectOptions] = useState()
  const { isLoading, refresh, rowData } = useGridFetch(api.getTrailers)
  const { updateTrailerMutation } = useTrailers()
  const { mutate: updateMutation } = updateTrailerMutation()
  useEffect(() => {
    const promises = [api.getTrailerMetadata(), api.getTractorMetadata()]
    Promise.all(promises).then((responses) => {
      const [configurationResponse, depotResponse] = responses
      const { data: configData } = configurationResponse
      const { data: depotData } = depotResponse
      setSelectOptions({ configurationOptions: configData.names, depotOptions: depotData.depots })
    })
  }, [])
  const { scopes } = useSecurityContext()
  const canEdit = scopes.admin?.vehicle_write
  const { updateSetting } = useUserSettings()

  const columnDefs = useMemo(() => (selectOptions ? getColumnDefs(selectOptions) : []), [selectOptions])
  const getRowId = useCallback((row) => row?.data?.id, [])
  const agPropOverrides = useMemo(
    () => ({
      getRowId,
    }),
    []
  )
  const createWrapper = useCallback(async (trailer) => {
    if (trailer?.trailer_number) trailer.trailer_number = trailer.trailer_number.toString()
    const { data: id } = await api.createTrailer(trailer)
    return new Promise((resolve) => {
      resolve({ data: { ...trailer, id } })
    })
  }, [])

  return (
    <GraviGrid
      controlBarProps={{
        title: 'Trailer',
        actionButtons: (
          <UploadDownloadButtons
            refreshCallback={refresh}
            downloadEP={api.downloadTrailers}
            uploadEP={canEdit && api.uploadTrailers}
            fileName='Trailers.xlsx'
          />
        ),
      }}
      agPropOverrides={{
        columnDefs: selectOptions && getColumnDefs(selectOptions),
      }}
      storageKey={STORAGE_KEY}
      rowData={rowData}
      loading={isLoading}
      updateEP={canEdit && updateMutation}
      createEP={canEdit && createWrapper}
      createConfig={createTrailerConfig}
      createSelectOptions={selectOptions}
      shouldInsertCreated
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}

const getColumnDefs = (selectOptions) => [
  {
    field: 'trailer_number',
    headerName: 'Trailer Number',
    valueSetter: validateInt,
  },
  {
    field: 'configuration',
    headerName: 'Configuration',
    cellEditor: 'agSelectCellEditor',
    cellEditorParams: {
      values: selectOptions.configurationOptions,
    },
  },
  {
    field: 'depot',
    cellEditorPopup: true,
    cellEditor: 'agRichSelectCellEditor',
    valueSetter: validateNotEmptyString,
    cellEditorParams: {
      values: selectOptions.depotOptions.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)),
    },
  },
  {
    field: 'make',
    headerName: 'Make',
    valueSetter: validateNotEmptyString,
  },
  {
    field: 'model',
    headerName: 'Model',
    valueSetter: validateNotEmptyString,
  },
  {
    field: 'weight',
    valueSetter: validateFloat,
  },
]
